/*
 * PROTECTED FILE — DO NOT MODIFY WITH LOVABLE AI OR ANY AI CODE TOOL.
 *
 * POST-MIGRATION NOTE (TanStack Start): <title>, description, canonical,
 * og:* and twitter:* are now emitted server-side by each route's head()
 * (see src/lib/seo/head.ts + src/lib/seo/routeMeta.ts) so non-JS crawlers
 * read them without running JavaScript. PageMeta must NOT re-emit those
 * tags — Helmet would inject a second canonical on the client and Google
 * discards conflicting canonicals.
 *
 * PageMeta now owns only what route head() does not emit: page-level
 * JSON-LD, article:* metadata, and the RSS alternate link. Its props API is
 * unchanged so the ~95 pages that call it keep compiling.
 */
import { Helmet } from "@/lib/helmet-compat";

interface PageMetaProps {
  title: string;
  description: string;
  path?: string;
  type?: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Article-specific metadata */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  /** Optional RSS feed URL to add as <link rel="alternate"> */
  rssUrl?: string;
  /** When true, injects noindex/nofollow/noarchive robots meta */
  noIndex?: boolean;
}

const BASE_URL = "https://www.homeapproach.com";

const PageMeta = ({ title, description, path = "/", type = "website", image, jsonLd, article, rssUrl, noIndex }: PageMetaProps) => {
  // Routes that already emit their JSON-LD from head() — skip here so the
  // page never carries two copies of the same graph.
  const headOwnsJsonLd =
    path === "/" || path.startsWith("/blog/") || path.startsWith("/guides/");

  const jsonLdString =
    jsonLd && !headOwnsJsonLd
      ? JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)
      : null;

  return (
    <Helmet>
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {article?.tags?.map((tag) => (
        <meta property="article:tag" content={tag} key={tag} />
      ))}
      {rssUrl && (
        <link rel="alternate" type="application/rss+xml" title="Home Approach Blog RSS" href={rssUrl} />
      )}
      {jsonLdString && (
        <script type="application/ld+json">{jsonLdString}</script>
      )}
    </Helmet>
  );
};

export default PageMeta;